<template>
  <div class="venva-editor" :style="containerStyle">
    <!-- Loading -->
    <div v-if="loading" class="loading-screen">
      <div class="spinner"></div>
    </div>

    <!-- Editor Fullscreen -->
    <div v-else-if="editorOpen && activeFunnel" class="editor-fullscreen">
      <!-- Header -->
      <div class="venva-header">
        <button @click="editorOpen=false" class="btn-back">
          <span class="icon">←</span> Voltar
        </button>
        <div class="funnel-title-wrapper">
          <input :value="activeFunnel.name" @input="activeFunnel.name=$event.target.value" class="funnel-title-input" title="Editar nome"/>
        </div>
        <button @click="activeFunnel.isActive=!activeFunnel.isActive" class="toggle-status">
          <span class="toggle-icon">{{ activeFunnel.isActive ? '🟢' : '⚪' }}</span>
          <span class="toggle-label">{{ activeFunnel.isActive ? 'Ativo' : 'Pausado' }}</span>
        </button>
        <button @click="showConfig=true" class="btn-config">
          <span class="icon">⚙️</span> Configurações
        </button>
        <div class="spacer"></div>
        <span class="save-status">{{ saved ? '✓ Salvo' : 'Salvo automaticamente' }}</span>
        <button @click="showSimulator=true" class="btn-test">▶ Testar</button>
        <button @click="handleSave" :disabled="saving" class="btn-publish">{{ saving ? 'Salvando…' : 'Publicar' }}</button>
      </div>

      <!-- Main Content -->
      <div class="editor-main">
        <!-- Palette Button (Top Left) -->
        <button @click="paletteOpen=!paletteOpen" class="palette-toggle" :class="{ active: paletteOpen }">
          <span class="icon">⊞</span> Componentes <span class="arrow">{{ paletteOpen ? '▲' : '▼' }}</span>
        </button>

        <!-- Palette Panel -->
        <div v-if="paletteOpen" class="palette-panel">
          <div class="palette-grid">
            <button v-for="item in PALETTE" :key="item.type" @click="addStepFromPalette(item)" class="palette-item" :style="{ '--item-color': item.color }">
              <span class="item-icon">{{ item.icon }}</span>
              <span class="item-label">{{ item.label }}</span>
            </button>
          </div>
        </div>

        <!-- Canvas Area -->
        <div ref="canvasRef" class="canvas-area" @wheel.prevent="onWheel($event)">
          <div class="canvas-inner" :style="{ width: flowSize.w+'px', height: flowSize.h+'px', transform: 'scale('+zoom+')', transformOrigin: '0 0' }">
            <!-- SVG Connections -->
            <svg :width="flowSize.w" :height="flowSize.h" class="connections-layer">
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                  <polygon points="0 0, 10 3, 0 6" fill="#ef4444" />
                </marker>
              </defs>
              <template v-for="step in (activeFunnel.steps || [])" :key="'conn-'+step.id">
                <path v-if="step.nextStepId && findStep(step.nextStepId)"
                      :d="connectionPath(step, findStep(step.nextStepId))"
                      fill="none" stroke="#ef4444" stroke-width="2" marker-end="url(#arrowhead)"/>
                <template v-for="(br,bi) in (step.branches || [])" :key="'br-'+step.id+'-'+bi">
                  <path v-if="br.nextStepId && findStep(br.nextStepId)"
                        :d="connectionPath(step, findStep(br.nextStepId), bi)"
                        fill="none" :stroke="BRANCH_COLORS[bi % BRANCH_COLORS.length]" stroke-width="2" marker-end="url(#arrowhead)"/>
                </template>
              </template>
            </svg>

            <!-- Nodes -->
            <div v-for="(step, idx) in (activeFunnel.steps || [])" :key="step.id"
                 class="venva-node" :class="{ selected: selectedStepId === step.id, dragging: dragId === step.id }"
                 :style="nodeStyle(step)"
                 @mousedown.stop="startDrag($event, step)" @click.stop="selectedStepId=step.id">
              <!-- Node Header -->
              <div class="node-header" :style="{ background: stepColor(step) }">
                <span class="node-icon">{{ stepIcon(step) }}</span>
                <span class="node-title">{{ step.name || 'Etapa ' + (idx+1) }}</span>
                <button @click.stop="removeStep(step.id)" class="node-delete">✕</button>
              </div>
              <!-- Node Body -->
              <div class="node-body">
                <div v-for="(act, ai) in (step.actions || [])" :key="act.id" class="action-row">
                  <select :value="act.type" @change="act.type=$event.target.value; markDirty()" class="action-select">
                    <option v-for="t in ACT_TYPES" :key="t" :value="t">{{ ACT_LABELS[t] }}</option>
                  </select>
                  <textarea v-if="act.type==='send_text'" v-model.lazy="act.value" placeholder="Mensagem..." rows="2" class="action-input" @input="markDirty()"></textarea>
                  <input v-else-if="act.type==='wait' || act.type==='delay'" type="number" :value="act.delaySeconds||0" @change="act.delaySeconds=Number($event.target.value); markDirty()" placeholder="Segundos" class="action-input"/>
                  <input v-else :value="act.value||''" @change="act.value=$event.target.value; markDirty()" :placeholder="actPlaceholder(act.type)" class="action-input"/>
                  <div class="action-controls">
                    <button @click="step.actions.splice(ai,1); markDirty()" class="ctrl-btn" title="Remover">−</button>
                    <button @click="dupAction(step,ai)" class="ctrl-btn" title="Duplicar">⧉</button>
                  </div>
                </div>
                <!-- Add Action -->
                <div class="add-action">
                  <select v-model="newActType" class="add-select">
                    <option disabled value="">+ Ação...</option>
                    <option v-for="t in ACT_TYPES" :key="t" :value="t">{{ ACT_LABELS[t] }}</option>
                  </select>
                  <button @click="addAct(step)" class="add-btn">OK</button>
                </div>
                <!-- Branches -->
                <div v-if="(step.branches||[]).length" class="branches-section">
                  <div v-for="(br,bi) in (step.branches||[])" :key="bi" class="branch-item" :style="{ borderLeftColor: BRANCH_COLORS[bi%6] }">
                    <input :value="br.conditionLabel||''" @change="br.conditionLabel=$event.target.value; markDirty()" placeholder="Condição..." class="branch-input"/>
                    <select :value="br.nextStepId||''" @change="br.nextStepId=$event.target.value; markDirty()" class="branch-select">
                      <option value="">→ Sem conexão</option>
                      <option v-for="s in activeFunnel.steps.filter(x=>x.id!==step.id)" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                    <button @click="step.branches.splice(bi,1); markDirty()" class="branch-del">✕</button>
                  </div>
                </div>
                <button @click="addBr(step)" class="add-branch">+ Ramificação</button>
              </div>
              <!-- Connection Ports -->
              <div class="port port-in"></div>
              <div class="port port-out"></div>
            </div>
          </div>
        </div>

        <!-- Zoom Controls (Right) -->
        <div class="zoom-controls">
          <button @click="zoom=Math.min(2.5,zoom+.1)" class="zoom-btn">+</button>
          <button @click="zoom=Math.max(.3,zoom-.1)" class="zoom-btn">−</button>
          <button @click="zoom=1" class="zoom-btn" title="Reset">⊡</button>
          <button @click="fitView" class="zoom-btn" title="Ajustar">⊞</button>
        </div>

        <!-- Bottom Toolbar -->
        <div class="bottom-toolbar">
          <button @click="organize" class="tool-btn">⚡ Organizar</button>
          <button @click="addStep" class="tool-btn">+ Etapa</button>
        </div>

        <!-- Minimap (Bottom Right) -->
        <div class="minimap">
          <div class="minimap-inner" :style="{ width: flowSize.w/10+'px', height: flowSize.h/10+'px' }">
            <div v-for="step in (activeFunnel.steps||[])" :key="'map-'+step.id" class="minimap-node" :style="{ left: (step.x||0)/10+'px', top: (step.y||0)/10+'px', background: stepColor(step) }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Funnel List -->
    <div v-else class="funnel-list">
      <div class="list-header">
        <h2>Chatbot</h2>
        <p>Configure funis de atendimento automático</p>
        <button @click="addFunnel" class="btn-primary">+ Novo Funil</button>
      </div>
      <div class="funnel-grid">
        <div v-for="f in funnels" :key="f.id" class="funnel-card">
          <div class="card-head">
            <strong>{{ f.name }}</strong>
            <span :class="['badge', f.isActive?'b-on':'b-off']">{{ f.isActive?'Ativo':'Inativo' }}</span>
          </div>
          <div class="card-meta">Gatilho: {{ TRIG_LABELS[f.trigger] || f.trigger }}<span v-if="f.triggerValue"> — {{ f.triggerValue }}</span><br/>Etapas: {{ f.steps.length }}</div>
          <div class="card-actions">
            <button @click="openEditor(f)" class="act-btn act-edit">Abrir editor</button>
            <button @click="f.isActive=!f.isActive" class="act-btn">{{ f.isActive?'Desativar':'Ativar' }}</button>
            <button @click="dupFunnel(f)" class="act-btn">Duplicar</button>
            <button @click="delFunnel(f)" class="act-btn act-del">Excluir</button>
          </div>
        </div>
        <div v-if="!funnels.length" class="empty-state">
          <p>Nenhum funil criado. Clique em "+ Novo Funil" para começar.</p>
        </div>
      </div>
      <div class="list-footer">
        <button @click="handleSave" :disabled="saving" class="btn-save">{{ saving?'Salvando…':'Salvar' }}</button>
      </div>
    </div>

    <!-- Config Modal -->
    <div v-if="showConfig" class="modal-overlay" @click="showConfig=false">
      <div class="modal" @click.stop>
        <div class="modal-head">
          <h3>⚙️ Configurações do Funil</h3>
          <button @click="showConfig=false" class="modal-close">×</button>
        </div>
        <div class="modal-body" v-if="activeFunnel">
          <div class="field">
            <label>Nome</label>
            <input v-model="activeFunnel.name" class="modal-input"/>
          </div>
          <div class="field">
            <label>Gatilho</label>
            <select v-model="activeFunnel.trigger" class="modal-input">
              <option value="always">Sempre</option>
              <option value="keyword">Palavra-chave</option>
              <option value="first_contact">Primeiro contato</option>
              <option value="inactivity">Inatividade</option>
              <option value="menu_option">Opção de menu</option>
            </select>
          </div>
          <div class="field" v-if="activeFunnel.trigger!=='always'">
            <label>Valor</label>
            <input v-model="activeFunnel.triggerValue" class="modal-input"/>
          </div>
          <div class="field">
            <label><input type="checkbox" v-model="activeFunnel.allowGroups"/> Permitir grupos</label>
          </div>
          <div class="field">
            <label><input type="checkbox" v-model="activeFunnel.onlyNewLeads"/> Apenas novos leads</label>
          </div>
          <div class="field">
            <label>Instruções IA</label>
            <textarea v-model="activeFunnel.aiInstructions" rows="3" class="modal-input"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Simulator Modal -->
    <div v-if="showSimulator" class="modal-overlay" @click="showSimulator=false">
      <div class="modal" @click.stop>
        <div class="modal-head">
          <h3>🤖 Simulador — {{ activeFunnel?.name }}</h3>
          <button @click="showSimulator=false" class="modal-close">×</button>
        </div>
        <div ref="chatRef" class="chat-area">
          <div v-if="!simMsgs.length" class="chat-empty">Digite uma mensagem para simular</div>
          <div v-for="(m,i) in simMsgs" :key="i" :class="['chat-bubble', m.role]">{{ m.text }}</div>
          <div v-if="simLoading" class="chat-bubble bot loading">⏳ Processando...</div>
        </div>
        <div class="chat-input">
          <input v-model="simInput" @keyup.enter="sendSim" placeholder="Mensagem..." class="sim-input"/>
          <button @click="sendSim" :disabled="simLoading||!simInput.trim()" class="btn-send">Enviar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import chatbotApi from '../../../api/chatbot';

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const funnels = ref([]);
const loading = ref(true);
const saving = ref(false);
const saved = ref(false);
const activeFunnelId = ref('');
const editorOpen = ref(false);
const paletteOpen = ref(false);
const selectedStepId = ref(null);
const zoom = ref(1);
const dragId = ref(null);
const dragOff = ref({ x: 0, y: 0 });
const canvasRef = ref(null);
const newActType = ref('send_text');
let saveTimer = null;

const showConfig = ref(false);
const showSimulator = ref(false);
const simMsgs = ref([]);
const simInput = ref('');
const simLoading = ref(false);
const pendingStepId = ref(null);
const chatRef = ref(null);

const CARD_H = 140;
const CARD_W = 220;
const BRANCH_COLORS = ['#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
const TRIG_LABELS = { first_contact:'Primeiro Contato', keyword:'Palavra-chave', inactivity:'Inatividade', menu_option:'Opção de Menu', always:'Sempre' };

const ACT_TYPES = ['send_text','send_image','send_audio','send_video','send_document','send_sticker','send_contact','send_location','webhook','send_buttons','send_list','wait','assign_tag','transfer_attendant','go_to_step','ai_generate','send_pix','wait_payment','whatsapp_label','fake_call','meta_pixel','delay'];
const ACT_LABELS = { send_text:'Texto', send_image:'Imagem', send_audio:'Áudio', send_video:'Vídeo', send_document:'Documento', send_sticker:'Figurinha', send_contact:'Contato', send_location:'Local', webhook:'Webhook', send_buttons:'Botões', send_list:'Lista', wait:'Aguardar', assign_tag:'Tag', transfer_attendant:'Transferir', go_to_step:'Ir para', ai_generate:'IA', send_pix:'PIX', wait_payment:'Ag. Pagto', whatsapp_label:'Etiqueta', fake_call:'Fake Call', meta_pixel:'Pixel', delay:'Atraso' };
const ACT_COLORS = { send_text:'#3b82f6', send_image:'#9333ea', send_audio:'#f97316', send_video:'#e11d48', send_document:'#dc2626', send_sticker:'#ec4899', send_contact:'#06b6d4', send_location:'#22c55e', webhook:'#64748b', send_buttons:'#7c3aed', send_list:'#14b8a6', wait:'#eab308', assign_tag:'#a855f7', transfer_attendant:'#0f766e', go_to_step:'#a855f7', ai_generate:'#06b6d4', send_pix:'#32BCAD', wait_payment:'#15803d', whatsapp_label:'#15803d', fake_call:'#d97706', meta_pixel:'#0369a1', delay:'#eab308' };
const ACT_ICONS = { send_text:'💬', send_image:'🖼️', send_audio:'🎤', send_video:'🎥', send_document:'📄', send_sticker:'😊', send_contact:'👤', send_location:'📍', webhook:'🔗', send_buttons:'🔘', send_list:'📋', wait:'⏱️', assign_tag:'🏷️', transfer_attendant:'🎧', go_to_step:'➡️', ai_generate:'✨', send_pix:'💲', wait_payment:'💳', whatsapp_label:'🏷️', fake_call:'📞', meta_pixel:'🎯', delay:'⏰' };

const PALETTE = [
  { icon:'💬', label:'Texto', type:'send_text', color:'#3b82f6' },
  { icon:'🖼️', label:'Imagem', type:'send_image', color:'#9333ea' },
  { icon:'🎤', label:'Áudio', type:'send_audio', color:'#f97316' },
  { icon:'🎥', label:'Vídeo', type:'send_video', color:'#e11d48' },
  { icon:'📄', label:'Doc', type:'send_document', color:'#dc2626' },
  { icon:'🔘', label:'Botões', type:'send_buttons', color:'#7c3aed' },
  { icon:'📋', label:'Lista', type:'send_list', color:'#14b8a6' },
  { icon:'🔗', label:'Webhook', type:'webhook', color:'#64748b' },
  { icon:'💲', label:'PIX', type:'send_pix', color:'#32BCAD' },
  { icon:'💳', label:'Pagto', type:'wait_payment', color:'#15803d' },
  { icon:'🏷️', label:'Tag', type:'assign_tag', color:'#a855f7' },
  { icon:'🎧', label:'Transfer', type:'transfer_attendant', color:'#0f766e' },
  { icon:'⏱️', label:'Delay', type:'delay', color:'#eab308' },
  { icon:'✨', label:'IA', type:'ai_generate', color:'#06b6d4' },
  { icon:'📞', label:'Fake Call', type:'fake_call', color:'#d97706' },
  { icon:'🎯', label:'Pixel', type:'meta_pixel', color:'#0369a1' },
];

const activeFunnel = computed(() => funnels.value.find(f => f.id === activeFunnelId.value) || null);

const containerStyle = computed(() => ({
  display: 'flex', gap: editorOpen.value ? 0 : 16,
  height: editorOpen.value ? '100vh' : 'calc(100vh - 200px)',
  position: editorOpen.value ? 'fixed' : undefined,
  top: 0, left: 0, right: 0, bottom: 0,
  zIndex: editorOpen.value ? 1050 : undefined,
  background: editorOpen.value ? '#09090b' : undefined,
  overflow: editorOpen.value ? 'hidden' : undefined,
  padding: editorOpen.value ? 0 : undefined,
  width: editorOpen.value ? '100vw' : undefined, maxWidth: editorOpen.value ? '100vw' : undefined, margin: editorOpen.value ? 0 : undefined
}));

const flowSize = computed(() => {
  let mx = 400, my = 400;
  for (const s of (activeFunnel.value?.steps || [])) {
    if ((s.x||0) + CARD_W > mx) mx = s.x + CARD_W;
    if ((s.y||0) + CARD_H + 60 > my) my = s.y + CARD_H + 60;
  }
  return { w: mx + 300, h: my + 300 };
});

function uid() { return Math.random().toString(36).slice(2, 10); }
function findStep(id) { return activeFunnel.value?.steps?.find(s => s.id === id); }
function stepColor(step) { return ACT_COLORS[(step.actions||[])[0]?.type] || '#3b82f6'; }
function stepIcon(step) { return ACT_ICONS[(step.actions||[])[0]?.type] || '💬'; }
function actPlaceholder(t) { return { send_image:'URL...', send_video:'URL...', send_document:'URL...', send_audio:'URL...', webhook:'https://...', send_pix:'Chave...', send_contact:'+55...', send_location:'Lat,Long', fake_call:'Número...', assign_tag:'Tag...', meta_pixel:'Valor...', go_to_step:'Step ID...', send_sticker:'URL...' }[t] || 'Valor...'; }

function nodeStyle(step) {
  return { left: (step.x||0)+'px', top: (step.y||0)+'px', width: CARD_W+'px' };
}

function connectionPath(from, to, branchIdx = 0) {
  const sx = (from.x||0)+CARD_W, sy = (from.y||0)+CARD_H/2;
  const ex = to.x||0, ey = (to.y||0)+CARD_H/2;
  const mx = (sx+ex)/2;
  return `M ${sx} ${sy} C ${mx} ${sy}, ${mx} ${ey}, ${ex} ${ey}`;
}

function markDirty() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(autoSave, 1000);
}

async function autoSave() {
  try {
    await chatbotApi.saveFunnels(accountId.value, { funnels: funnels.value, deletedFunnelIds: [] });
    saved.value = true;
    setTimeout(() => saved.value = false, 2000);
  } catch (e) { console.error('Auto-save err:', e); }
}

async function handleSave() {
  saving.value = true;
  try {
    const { data } = await chatbotApi.saveFunnels(accountId.value, { funnels: funnels.value, deletedFunnelIds: [] });
    if (data?.funnels) funnels.value = data.funnels;
    saved.value = true;
    setTimeout(() => saved.value = false, 3000);
  } catch (e) { alert('Erro ao salvar'); }
  finally { saving.value = false; }
}

function addStepFromPalette(item) {
  if (!activeFunnel.value) return;
  const last = activeFunnel.value.steps[activeFunnel.value.steps.length - 1];
  activeFunnel.value.steps.push({
    id: `step-${uid()}`, name: ACT_LABELS[item.type] + ' ' + (activeFunnel.value.steps.length+1),
    trigger: 'always', triggerValue: '', position: activeFunnel.value.steps.length,
    awaitUser: false, nextStepId: '',
    actions: [{ id: uid(), type: item.type, value: '', options: [], delaySeconds: item.type==='delay'||item.type==='wait'?3:0 }],
    branches: [], x: (last?.x||60)+260, y: Math.min((last?.y||40)+40, 600)
  });
  markDirty();
}

function addStep() {
  if (!activeFunnel.value) return;
  const last = activeFunnel.value.steps[activeFunnel.value.steps.length - 1];
  activeFunnel.value.steps.push({
    id: `step-${uid()}`, name: 'Etapa ' + (activeFunnel.value.steps.length+1),
    trigger: 'always', triggerValue: '', position: activeFunnel.value.steps.length,
    awaitUser: true, nextStepId: '',
    actions: [{ id: uid(), type: 'send_text', value: '', options: [], delaySeconds: 0 }],
    branches: [], x: (last?.x||60)+260, y: last?.y||40
  });
  markDirty();
}

function removeStep(id) {
  if (!confirm('Excluir etapa?')) return;
  activeFunnel.value.steps = activeFunnel.value.steps.filter(s => s.id !== id);
  activeFunnel.value.steps.forEach(s => { if (s.nextStepId === id) s.nextStepId = ''; s.branches = (s.branches||[]).filter(b => b.nextStepId !== id); });
  if (selectedStepId.value === id) selectedStepId.value = null;
  markDirty();
}

function addAct(step) { if (!newActType.value) return; step.actions.push({ id: uid(), type: newActType.value, value: '', options: [], delaySeconds: 0 }); newActType.value = 'send_text'; markDirty(); }
function dupAction(step, i) { const c = JSON.parse(JSON.stringify(step.actions[i])); c.id = uid(); step.actions.splice(i+1, 0, c); markDirty(); }
function addBr(step) { step.branches.push({ conditionLabel: '', nextStepId: '' }); markDirty(); }

function organize() {
  if (!activeFunnel.value) return;
  activeFunnel.value.steps.forEach((s, i) => { s.x = 60 + Math.floor(i/2)*280; s.y = 40 + (i%2)*280; });
  markDirty();
}

function fitView() { zoom.value = 1; }

// Drag
function startDrag(e, step) {
  if (e.button !== 0) return;
  dragId.value = step.id; selectedStepId.value = step.id;
  const r = canvasRef.value.getBoundingClientRect();
  dragOff.value = { x: (e.clientX-r.left)/zoom.value - (step.x||0), y: (e.clientY-r.top)/zoom.value - (step.y||0) };
  e.preventDefault();
}
function onMove(e) {
  if (!dragId.value || !canvasRef.value) return;
  const s = findStep(dragId.value); if (!s) return;
  const r = canvasRef.value.getBoundingClientRect();
  s.x = (e.clientX-r.left)/zoom.value - dragOff.value.x;
  s.y = (e.clientY-r.top)/zoom.value - dragOff.value.y;
}
function onUp() { if (dragId.value) { markDirty(); dragId.value = null; } }
function onWheel(e) { zoom.value = Math.min(2.5, Math.max(.3, zoom.value * (e.deltaY < 0 ? 1.1 : .9))); }

// Funnel CRUD
function openEditor(f) { activeFunnelId.value = f.id; editorOpen.value = true; selectedStepId.value = f.steps?.[0]?.id || null; }
function addFunnel() {
  const id = `f-${uid()}`;
  funnels.value.push({ id, name:'Novo Funil', isActive:false, trigger:'keyword', triggerValue:'', instanceId:'', allowGroups:true, onlyNewLeads:false,
    steps:[{ id:`s-${uid()}`, name:'Início', trigger:'always', triggerValue:'', position:0, awaitUser:true, nextStepId:'', actions:[{ id:uid(), type:'send_text', value:'', options:[], delaySeconds:0 }], branches:[], x:60, y:40 }],
    aiInstructions:'', aiConversational:false, allowedPhoneNumber:'' });
  openEditor(funnels.value[funnels.value.length-1]);
}
function dupFunnel(f) { const c = JSON.parse(JSON.stringify(f)); c.id = `f-${uid()}`; c.name += ' (cópia)'; c.steps.forEach(s => s.id = `s-${uid()}`); funnels.value.push(c); }
function delFunnel(f) { if (!confirm('Excluir funil?')) return; funnels.value = funnels.value.filter(x => x.id !== f.id); if (activeFunnelId.value === f.id) activeFunnelId.value = funnels.value[0]?.id || ''; }

// API
async function load() {
  try {
    const { data } = await chatbotApi.getFunnels(accountId.value);
    const list = Array.isArray(data) ? data : [];
    list.forEach(f => f.steps.forEach((s, i) => {
      s.x = Number(s.x)||60; s.y = Number(s.y); if (isNaN(s.y)) s.y = i*220;
      if (!s.actions) s.actions = []; if (!s.branches) s.branches = [];
      s.actions.forEach(a => { if (!a.id) a.id = uid(); });
    }));
    funnels.value = list;
    if (list.length) activeFunnelId.value = list[0].id;
  } catch (e) { console.error(e); } finally { loading.value = false; }
}

// Simulator
async function sendSim() {
  if (!simInput.value.trim() || !activeFunnel.value) return;
  simMsgs.value.push({ role: 'user', text: simInput.value }); simInput.value = ''; simLoading.value = true;
  try {
    const { data } = await chatbotApi.simulate(accountId.value, { funnelId: activeFunnel.value.id, message: simMsgs.value[simMsgs.value.length-1].text, pendingStepId: pendingStepId.value, history: simMsgs.value.map(m => ({ role: m.role, content: m.text })) });
    pendingStepId.value = data.nextPendingStepId;
    (data.responses||[]).forEach(r => simMsgs.value.push({ role: 'bot', text: r.content || '['+r.type+']' }));
  } catch (e) { simMsgs.value.push({ role:'bot', text:'Erro: '+(e?.message||'') }); }
  finally { simLoading.value = false; nextTick(() => { if (chatRef.value) chatRef.value.scrollTop = chatRef.value.scrollHeight; }); }
}

onMounted(() => {
  load();
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
});
</script>

<style scoped>
/* Venva-style Dark Theme */
.venva-editor { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #e4e4e7; }
.loading-screen { display:flex; justify-content:center; align-items:center; min-height:50vh; }
.spinner { width:40px; height:40px; border:4px solid #27272a; border-top-color:#ef4444; border-radius:50%; animation:spin 1s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* Editor Fullscreen */
.editor-fullscreen { position:fixed; inset:0; z-index:9999; background:#09090b; display:flex; flex-direction:column; }
.venva-header { height:56px; border-bottom:1px solid #27272a; display:flex; align-items:center; padding:0 16px; background:#111111; gap:12px; flex-shrink:0; }
.btn-back { background:#1a1a1a; border:1px solid #27272a; color:#e4e4e7; cursor:pointer; font-size:13px; padding:8px 14px; border-radius:6px; display:flex; align-items:center; gap:6px; }
.btn-back:hover { background:#27272a; }
.funnel-title-wrapper { border:1px solid #ef4444; border-radius:6px; padding:0 8px; }
.funnel-title-input { background:transparent; border:none; color:#fff; font-size:15px; font-weight:600; padding:6px 0; outline:none; width:200px; }
.toggle-status { background:none; border:none; cursor:pointer; display:flex; align-items:center; gap:6px; color:#e4e4e7; font-size:13px; }
.toggle-icon { font-size:16px; }
.btn-config { background:#1a1a1a; border:1px solid #27272a; color:#e4e4e7; cursor:pointer; font-size:13px; padding:8px 14px; border-radius:6px; display:flex; align-items:center; gap:6px; }
.btn-config:hover { background:#27272a; }
.spacer { flex:1; }
.save-status { color:#10b981; font-size:12px; }
.btn-test { background:#27272a; color:#e4e4e7; border:none; padding:8px 16px; border-radius:6px; cursor:pointer; font-weight:600; font-size:13px; }
.btn-publish { background:#ef4444; color:#fff; border:none; padding:8px 20px; border-radius:20px; cursor:pointer; font-weight:700; font-size:13px; }
.btn-publish:disabled { opacity:.6; }

/* Editor Main */
.editor-main { flex:1; position:relative; overflow:hidden; background-image:radial-gradient(circle,#27272a 1px,transparent 1px); background-size:24px 24px; background-color:#09090b; }

/* Palette Toggle */
.palette-toggle { position:absolute; top:16px; left:16px; background:#ef4444; color:#fff; border:none; padding:10px 16px; border-radius:6px; cursor:pointer; font-weight:700; font-size:14px; display:flex; align-items:center; gap:8px; z-index:20; }
.palette-toggle.active { background:#dc2626; }
.palette-toggle .arrow { font-size:10px; }

/* Palette Panel */
.palette-panel { position:absolute; top:60px; left:16px; background:#111111; border:1px solid #27272a; border-radius:8px; padding:12px; z-index:20; max-width:320px; }
.palette-grid { display:grid; grid-template-columns:repeat(4, 1fr); gap:8px; }
.palette-item { background:#1a1a1a; border:1px solid #27272a; border-left:3px solid var(--item-color); color:#e4e4e7; padding:8px; border-radius:6px; cursor:pointer; display:flex; flex-direction:column; align-items:center; gap:4px; font-size:11px; }
.palette-item:hover { background:#27272a; }
.item-icon { font-size:20px; }
.item-label { font-weight:600; }

/* Canvas */
.canvas-area { width:100%; height:100%; overflow:auto; cursor:grab; }
.canvas-inner { position:relative; }
.connections-layer { position:absolute; top:0; left:0; pointer-events:none; }

/* Nodes */
.venva-node { position:absolute; border-radius:8px; overflow:hidden; box-shadow:0 4px 12px rgba(0,0,0,.5); cursor:grab; z-index:10; }
.venva-node.selected { box-shadow:0 0 0 2px #ef4444, 0 4px 12px rgba(0,0,0,.5); z-index:20; }
.venva-node.dragging { cursor:grabbing; z-index:30; }
.node-header { padding:8px 12px; display:flex; align-items:center; gap:8px; color:#fff; font-size:12px; font-weight:700; }
.node-icon { font-size:16px; }
.node-title { flex:1; }
.node-delete { background:none; border:none; color:#fff; cursor:pointer; font-size:14px; opacity:.7; }
.node-delete:hover { opacity:1; }
.node-body { background:#1e1e1e; padding:10px; }
.action-row { margin-bottom:8px; }
.action-select { width:100%; background:#09090b; border:1px solid #27272a; color:#e4e4e7; font-size:11px; padding:4px 6px; border-radius:4px; margin-bottom:4px; }
.action-input { width:100%; background:#09090b; border:1px solid #27272a; color:#fafafa; font-size:11px; padding:6px; border-radius:4px; box-sizing:border-box; resize:vertical; }
.action-controls { display:flex; gap:4px; margin-top:4px; }
.ctrl-btn { background:#27272a; border:none; color:#e4e4e7; cursor:pointer; font-size:12px; padding:2px 8px; border-radius:4px; }
.ctrl-btn:hover { background:#3f3f46; }
.add-action { display:flex; gap:4px; margin-top:8px; }
.add-select { flex:1; background:#09090b; border:1px solid #27272a; color:#71717a; font-size:11px; padding:4px 6px; border-radius:4px; }
.add-btn { background:#ef4444; color:#fff; border:none; padding:4px 12px; border-radius:4px; font-size:11px; cursor:pointer; }
.branches-section { margin-top:8px; padding-top:8px; border-top:1px solid #27272a; }
.branch-item { padding-left:8px; border-left:2px solid; margin-bottom:6px; display:flex; flex-wrap:wrap; gap:4px; align-items:center; }
.branch-input { background:#09090b; border:1px solid #27272a; color:#fafafa; font-size:10px; padding:3px 6px; border-radius:4px; flex:1; min-width:80px; }
.branch-select { background:#09090b; border:1px solid #27272a; color:#e4e4e7; font-size:10px; padding:3px 6px; border-radius:4px; flex:1; min-width:100px; }
.branch-del { background:none; border:none; color:#ef4444; cursor:pointer; font-size:12px; }
.add-branch { background:#27272a; border:none; color:#e4e4e7; padding:4px 10px; border-radius:4px; font-size:11px; cursor:pointer; margin-top:6px; }

/* Ports */
.port { position:absolute; width:10px; height:10px; border-radius:50%; background:#ef4444; border:2px solid #09090b; }
.port-in { left:-5px; top:50%; transform:translateY(-50%); }
.port-out { right:-5px; top:50%; transform:translateY(-50%); }

/* Zoom Controls */
.zoom-controls { position:absolute; right:16px; top:50%; transform:translateY(-50%); background:#18181b; border:1px solid #27272a; border-radius:20px; padding:4px; display:flex; flex-direction:column; gap:4px; z-index:20; }
.zoom-btn { background:none; border:none; color:#fff; cursor:pointer; font-size:18px; padding:8px; border-radius:50%; width:36px; height:36px; display:flex; align-items:center; justify-content:center; }
.zoom-btn:hover { background:#27272a; }

/* Bottom Toolbar */
.bottom-toolbar { position:absolute; bottom:16px; left:50%; transform:translateX(-50%); background:#18181b; border:1px solid #27272a; border-radius:20px; padding:4px; display:flex; gap:4px; z-index:20; }
.tool-btn { background:none; border:none; color:#e4e4e7; font-size:13px; padding:8px 16px; cursor:pointer; border-radius:16px; }
.tool-btn:hover { background:#27272a; }

/* Minimap */
.minimap { position:absolute; bottom:16px; right:16px; background:#18181b; border:2px solid #ef4444; border-radius:6px; padding:4px; z-index:20; }
.minimap-inner { position:relative; background:#09090b; }
.minimap-node { position:absolute; width:4px; height:4px; border-radius:1px; }

/* Funnel List */
.funnel-list { padding:24px; }
.list-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; }
.list-header h2 { margin:0; font-size:24px; }
.list-header p { margin:4px 0 0; font-size:13px; opacity:.7; }
.btn-primary { background:#ef4444; color:#fff; border:none; border-radius:20px; cursor:pointer; font-weight:700; padding:10px 20px; font-size:14px; }
.funnel-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
.funnel-card { background:#111111; border:1px solid #27272a; border-radius:12px; padding:20px; display:flex; flex-direction:column; gap:12px; }
.card-head { display:flex; justify-content:space-between; align-items:center; }
.badge { font-size:11px; padding:3px 10px; border-radius:12px; color:#fff; font-weight:600; }
.b-on { background:#10b981; }
.b-off { background:#64748b; }
.card-meta { font-size:13px; opacity:.7; }
.card-actions { display:flex; gap:8px; flex-wrap:wrap; }
.act-btn { background:#1a1a1a; border:1px solid #27272a; color:#e4e4e7; padding:6px 12px; font-size:12px; border-radius:6px; cursor:pointer; }
.act-edit { background:#ef4444; color:#fff; border:none; flex:1; font-weight:600; }
.act-del { background:#dc2626; color:#fff; border:none; }
.empty-state { background:#111111; border:1px solid #27272a; border-radius:12px; padding:40px; text-align:center; grid-column:1/-1; }
.list-footer { margin-top:20px; }
.btn-save { background:#10b981; color:#fff; border:none; border-radius:20px; cursor:pointer; font-weight:700; padding:8px 20px; font-size:14px; }

/* Modals */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.8); z-index:99998; display:flex; align-items:center; justify-content:center; }
.modal { width:500px; max-width:92vw; max-height:85vh; background:#111111; border:1px solid #27272a; border-radius:12px; display:flex; flex-direction:column; overflow:hidden; }
.modal-head { padding:16px 20px; border-bottom:1px solid #27272a; display:flex; justify-content:space-between; align-items:center; }
.modal-head h3 { margin:0; font-size:16px; }
.modal-close { background:none; border:none; color:#e4e4e7; cursor:pointer; font-size:24px; }
.modal-body { padding:20px; overflow-y:auto; }
.field { margin-bottom:16px; }
.field label { display:block; font-size:12px; color:#a1a1aa; margin-bottom:6px; }
.modal-input { width:100%; padding:8px 12px; background:#09090b; border:1px solid #27272a; color:#fff; border-radius:6px; font-size:13px; box-sizing:border-box; }

/* Chat Simulator */
.chat-area { flex:1; overflow-y:auto; padding:16px; display:flex; flex-direction:column; gap:10px; min-height:300px; }
.chat-empty { color:#52525b; font-size:13px; text-align:center; margin-top:40px; }
.chat-bubble { max-width:80%; padding:10px 14px; border-radius:12px; color:#fff; font-size:13px; white-space:pre-wrap; word-break:break-word; }
.chat-bubble.user { align-self:flex-end; background:#ef4444; }
.chat-bubble.bot { align-self:flex-start; background:#27272a; }
.loading { color:#a1a1aa; }
.chat-input { padding:12px 16px; border-top:1px solid #27272a; display:flex; gap:8px; }
.sim-input { flex:1; padding:10px 14px; background:#09090b; border:1px solid #27272a; color:#fff; border-radius:8px; font-size:14px; outline:none; }
.btn-send { background:#ef4444; color:#fff; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:700; }
.btn-send:disabled { opacity:.5; }
</style>
